// src/components/sizeCharts/SizeChartAdminPanel.js

import React, { useState, useEffect } from "react";
import {
    Edit,
    Trash2,
    Plus,
    X,
    AlertCircle,
    CheckCircle,
    Tag as TagIcon,
    ListOrdered,
    SlidersHorizontal,
    Copy,
} from "lucide-react";
import apiWithAuth from "../../services/apiWithAuth";

const SIZE_CHART_API_BASE = "/admin-panel/size-chart";

const defaultChart = {
    name: "",
    tags: [],
    labelName: "",
    labels: [],
    fields: [], // { name: string, units: { [unitKey]: true } }
    values: [], // { label: string, measurements: [ { [unitKey]: string } ] }
    shopId: "", // blank => global template
    imageUrl: "", // NEW: image url for chart
};

// Simple confirmation modal
function ConfirmationModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    children,
    requireConfirmationText = false,
}) {
    const [confirmationText, setConfirmationText] = useState("");

    useEffect(() => {
        if (isOpen) setConfirmationText("");
    }, [isOpen]);

    const isConfirmed =
        !requireConfirmationText ||
        confirmationText.trim().toLowerCase() === "confirm";

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg m-4">
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    <div className="mt-4 text-gray-600 whitespace-pre-wrap">
                        {children}
                    </div>

                    {requireConfirmationText && (
                        <div className="mt-4">
                            <label
                                htmlFor="confirmation-input"
                                className="block text-sm text-gray-700 mb-1"
                            >
                                Type <strong>confirm</strong> to proceed
                            </label>
                            <input
                                id="confirmation-input"
                                type="text"
                                value={confirmationText}
                                onChange={(e) => setConfirmationText(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                                placeholder="confirm"
                            />
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded bg-gray-200"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 rounded bg-red-600 text-white disabled:opacity-50"
                        disabled={!isConfirmed}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
}

// Helper to parse comma-separated text into a units object
const parseUnitsString = (value) => {
    const parts = value
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);
    const units = {};
    parts.forEach((u) => {
        units[u] = true;
    });
    return units;
};

// For a field's units object -> comma string
const unitsObjectToString = (units = {}) =>
    Object.keys(units)
        .filter((k) => units[k])
        .join(", ");

export default function SizeChartAdminPanel() {
    const [charts, setCharts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");

    // NEW: counts for global/shop charts
    const [globalCount, setGlobalCount] = useState(0);
    const [shopCount, setShopCount] = useState(0);

    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newChart, setNewChart] = useState(defaultChart);
    const [editingChart, setEditingChart] = useState(null);

    const [confirmationModal, setConfirmationModal] = useState({
        isOpen: false,
        title: "",
        message: "",
        onConfirm: () => { },
        requireConfirmationText: false,
    });

    const [tagInput, setTagInput] = useState("");
    const [searchText, setSearchText] = useState("");

    // NEW: image upload state
    const [imageUploading, setImageUploading] = useState(false);
    const [imageUploadError, setImageUploadError] = useState("");

    // NEW: pagination + global toggle
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [showGlobal, setShowGlobal] = useState(true); // default = global true

    // utilities to work with "current" chart (either creating or editing)
    const currentChart = editingChart || newChart;
    const setCurrentChart = editingChart ? setEditingChart : setNewChart;
    const isEditing = !!editingChart;

    const resetFormState = () => {
        setIsAddingNew(false);
        setEditingChart(null);
        setNewChart(defaultChart);
        setTagInput("");
        setImageUploading(false);
        setImageUploadError("");
    };

    // UPDATED: fetchCharts with pagination + global filter
    const fetchCharts = async (
        pageToLoad = page,
        limitToUse = limit,
        globalFlag = showGlobal
    ) => {
        setLoading(true);
        setError(null);
        try {
            const resp = await apiWithAuth.get(SIZE_CHART_API_BASE, {
                params: {
                    page: pageToLoad,
                    limit: limitToUse,
                    global: globalFlag ? "true" : "false",
                },
            });

            const data = resp.data || {};
            setCharts(data.data || []);
            setTotalCount(data.totalCount || 0);
            setPage(data.page || pageToLoad);
            setLimit(limitToUse);
            setShowGlobal(globalFlag);

            // 🔴 NEW: set counts from summaryCounts
            if (data.summaryCounts) {
                setGlobalCount(data.summaryCounts.globalChartsCount || 0);
                setShopCount(data.summaryCounts.chartsByShopCount || 0);
            }
        } catch (err) {
            console.error(err);
            setError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Failed to fetch size charts"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // initial load: global charts
        fetchCharts(1, limit, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(""), 4000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);

    // ---- IMAGE UPLOAD ----
    const handleImageChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImageUploadError("");
        setImageUploading(true);

        try {
            const formData = new FormData();
            formData.append("file", file);

            // api/upload/single  -> backend returns { success: true, file_url: "..." }
            const resp = await apiWithAuth.post("/upload/single", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const fileUrl = resp.data?.file_url;
            if (!fileUrl) {
                throw new Error("Image upload failed: file_url not returned");
            }

            // update current chart with imageUrl
            setCurrentChart((prev) => ({
                ...prev,
                imageUrl: fileUrl,
            }));
        } catch (err) {
            console.error(err);
            setImageUploadError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Failed to upload image"
            );
        } finally {
            setImageUploading(false);
        }
    };

    // ---- TAGS ----
    const handleAddTag = () => {
        const value = tagInput.trim();
        if (!value) return;
        const existingTags = currentChart.tags || [];
        if (existingTags.includes(value)) {
            setTagInput("");
            return;
        }
        setCurrentChart({
            ...currentChart,
            tags: [...existingTags, value],
        });
        setTagInput("");
    };

    const handleTagKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            handleAddTag();
        }
    };

    const handleRemoveTag = (tag) => {
        setCurrentChart({
            ...currentChart,
            tags: (currentChart.tags || []).filter((t) => t !== tag),
        });
    };

    // ---- LABELS & VALUES ----
    const ensureValuesAligned = (chart) => {
        const fields = chart.fields || [];
        const labels = chart.labels || [];
        const values = chart.values || [];

        const nextValues = labels.map((lbl, index) => {
            const existing = values[index];
            const existingMeasurements = existing?.measurements || [];
            const measurements = fields.map((f, fieldIdx) => {
                const unitKeys = Object.keys(f.units || {});
                const oldMeasurement = existingMeasurements[fieldIdx] || {};
                const newMeasurement = {};
                unitKeys.forEach((u) => {
                    newMeasurement[u] = oldMeasurement[u] || "";
                });
                return newMeasurement;
            });

            return {
                label: existing?.label || lbl,
                measurements,
            };
        });

        return {
            ...chart,
            values: nextValues,
        };
    };

    const handleAddLabel = (labelText) => {
        const label = (labelText || "").trim();
        if (!label) return;
        const labels = currentChart.labels || [];
        if (labels.includes(label)) return;

        let updated = {
            ...currentChart,
            labels: [...labels, label],
        };
        updated = ensureValuesAligned(updated);
        setCurrentChart(updated);
    };

    const handleRemoveLabel = (idx) => {
        const labels = [...(currentChart.labels || [])];
        const values = [...(currentChart.values || [])];
        labels.splice(idx, 1);
        values.splice(idx, 1);
        setCurrentChart({
            ...currentChart,
            labels,
            values,
        });
    };

    const handleLabelChange = (idx, newLabel) => {
        const labels = [...(currentChart.labels || [])];
        const values = [...(currentChart.values || [])];
        labels[idx] = newLabel;
        if (values[idx]) {
            values[idx] = { ...values[idx], label: newLabel };
        }
        setCurrentChart({
            ...currentChart,
            labels,
            values,
        });
    };

    // ---- FIELDS ----
    const handleAddField = () => {
        const fields = currentChart.fields || [];
        const newField = {
            name: `Field ${fields.length + 1}`,
            units: { inches: true, cm: true },
        };

        const newFields = [...fields, newField];

        let updated = {
            ...currentChart,
            fields: newFields,
        };
        updated = ensureValuesAligned(updated);
        setCurrentChart(updated);
    };

    const handleFieldNameChange = (idx, newName) => {
        const fields = [...(currentChart.fields || [])];
        if (!fields[idx]) return;
        fields[idx] = { ...fields[idx], name: newName };
        setCurrentChart(ensureValuesAligned({ ...currentChart, fields }));
    };

    const handleFieldUnitsChange = (idx, unitsString) => {
        const fields = [...(currentChart.fields || [])];
        if (!fields[idx]) return;
        const units = parseUnitsString(unitsString);
        fields[idx] = { ...fields[idx], units };
        setCurrentChart(ensureValuesAligned({ ...currentChart, fields }));
    };

    const handleRemoveField = (idx) => {
        const fields = [...(currentChart.fields || [])];
        const values = [...(currentChart.values || [])];
        fields.splice(idx, 1);

        const newValues = values.map((v) => {
            const measurements = [...(v.measurements || [])];
            measurements.splice(idx, 1);
            return { ...v, measurements };
        });

        setCurrentChart(
            ensureValuesAligned({
                ...currentChart,
                fields,
                values: newValues,
            })
        );
    };

    // ---- MEASUREMENTS TABLE ----
    const buildFieldUnitColumns = (chart) => {
        const fields = chart.fields || [];
        const cols = [];
        fields.forEach((field, fieldIdx) => {
            const unitKeys = Object.keys(field.units || {}).filter(
                (k) => field.units[k]
            );
            unitKeys.forEach((unitKey) => {
                cols.push({
                    fieldIndex: fieldIdx,
                    unitKey,
                    header: `${field.name || `Field ${fieldIdx + 1}`} (${unitKey})`,
                });
            });
        });
        return cols;
    };

    const handleMeasurementChange = (labelIdx, fieldIndex, unitKey, value) => {
        const values = [...(currentChart.values || [])];
        if (!values[labelIdx]) {
            values[labelIdx] = {
                label: currentChart.labels[labelIdx] || "",
                measurements: [],
            };
        }
        const row = { ...values[labelIdx] };
        const measurements = [...(row.measurements || [])];
        const oldMeasurement = measurements[fieldIndex] || {};
        const newMeasurement = { ...oldMeasurement, [unitKey]: value };
        measurements[fieldIndex] = newMeasurement;
        row.measurements = measurements;
        values[labelIdx] = row;

        setCurrentChart({
            ...currentChart,
            values,
        });
    };

    // ---- CRUD API ----
    const handleSaveChart = async (e) => {
        e?.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const alignedChart = ensureValuesAligned(currentChart);

            const payload = {
                name: alignedChart.name,
                tags: alignedChart.tags || [],
                labelName: alignedChart.labelName,
                labels: alignedChart.labels || [],
                fields: alignedChart.fields || [],
                values: alignedChart.values || [],
            };

            if (alignedChart.shopId && alignedChart.shopId.trim()) {
                payload.shopId = alignedChart.shopId.trim();
            }

            // NEW: include imageUrl if present
            if (alignedChart.imageUrl) {
                payload.imageUrl = alignedChart.imageUrl;
            }

            if (isEditing && editingChart._id) {
                await apiWithAuth.put(
                    `${SIZE_CHART_API_BASE}?chartId=${editingChart._id}`,
                    payload
                );
                setSuccessMessage("Size chart updated");
            } else {
                await apiWithAuth.post(SIZE_CHART_API_BASE, payload);
                setSuccessMessage("Size chart created");
            }

            resetFormState();
            // after save, reload first page of current view (global/shop)
            await fetchCharts(1, limit, showGlobal);
        } catch (err) {
            console.error(err);
            setError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Failed to save size chart"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteChart = async (chart) => {
        setLoading(true);
        setError(null);
        try {
            await apiWithAuth.delete(
                `${SIZE_CHART_API_BASE}?chartId=${chart._id}`
            );
            setSuccessMessage("Size chart deleted");
            if (editingChart && editingChart._id === chart._id) {
                resetFormState();
            }
            await fetchCharts(page, limit, showGlobal);
        } catch (err) {
            console.error(err);
            setError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Failed to delete size chart"
            );
        } finally {
            setLoading(false);
        }
    };

    // ---- LIST + SEARCH ----
    const filteredCharts = charts.filter((c) => {
        if (!searchText.trim()) return true;
        const q = searchText.trim().toLowerCase();
        return (
            c.name?.toLowerCase().includes(q) ||
            (c.tags || []).some((t) => t.toLowerCase().includes(q))
        );
    });

    const fieldUnitColumns = buildFieldUnitColumns(currentChart);

    const getScopeBadge = (chart) => {
        if (!chart.shopId) {
            return {
                label: "GLOBAL TEMPLATE",
                className:
                    "bg-purple-50 text-purple-700 border border-purple-200",
            };
        }
        return {
            label: "SHOP CHART",
            className: "bg-blue-50 text-blue-700 border-blue-200",
        };
    };

    const handleUseAsTemplate = (chart) => {
        setIsAddingNew(true);
        setEditingChart(null);
        setNewChart({
            ...defaultChart,
            ...chart,
            _id: undefined,
            name: `${chart.name} (Copy)`,
            shopId: "", // keep empty so it saves as global unless user fills
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // pagination helpers
    const totalPages = totalCount > 0 ? Math.ceil(totalCount / limit) : 1;
    const startIndex = totalCount === 0 ? 0 : (page - 1) * limit + 1;
    const endIndex = totalCount === 0 ? 0 : Math.min(page * limit, totalCount);

    const handlePrevPage = () => {
        if (page > 1) {
            fetchCharts(page - 1, limit, showGlobal);
        }
    };

    const handleNextPage = () => {
        if (page < totalPages) {
            fetchCharts(page + 1, limit, showGlobal);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
            <ConfirmationModal
                isOpen={confirmationModal.isOpen}
                onClose={() =>
                    setConfirmationModal((prev) => ({ ...prev, isOpen: false }))
                }
                onConfirm={confirmationModal.onConfirm}
                title={confirmationModal.title}
                requireConfirmationText={confirmationModal.requireConfirmationText}
            >
                {confirmationModal.message}
            </ConfirmationModal>

            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex flex-col gap-4 mb-4 sm:mb-6 w-full">
                    {/* 1. Title/Description Section */}
                    <div className="min-w-0">
                        <h1 className="text-xl sm:text-2xl font-bold">Size Chart Management</h1>
                        <p className="text-sm text-gray-600">
                            Create global size chart templates and shop-specific charts.
                        </p>
                    </div>

                    {/* 2. Actions/Controls Section */}
                    <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center w-full">
                        {/* Search Input */}
                        <input
                            type="text"
                            className="px-3 py-2 border rounded text-sm w-full sm:flex-1 sm:min-w-[180px]"
                            placeholder="Search by name or tag..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />

                        {/* Global / Shop toggle */}
                        <div className="inline-flex rounded border bg-white overflow-hidden text-xs w-full sm:w-auto">
                            <button
                                type="button"
                                className={`flex-1 sm:flex-none px-3 py-2 transition-colors ${showGlobal
                                    ? "bg-blue-600 text-white"
                                    : "bg-white text-gray-700 hover:bg-gray-50"
                                    }`}
                                onClick={() => {
                                    if (!showGlobal) {
                                        setPage(1);
                                        fetchCharts(1, limit, true);
                                    }
                                }}
                            >
                                Global ({globalCount})
                            </button>
                            <button
                                type="button"
                                className={`flex-1 sm:flex-none px-3 py-2 transition-colors ${!showGlobal
                                    ? "bg-blue-600 text-white"
                                    : "bg-white text-gray-700 hover:bg-gray-50"
                                    }`}
                                onClick={() => {
                                    if (showGlobal) {
                                        setPage(1);
                                        fetchCharts(1, limit, false);
                                    }
                                }}
                            >
                                Shop ({shopCount})
                            </button>
                        </div>

                        {/* Add New Button */}
                        <button
                            onClick={() => {
                                if (isAddingNew) {
                                    resetFormState();
                                } else {
                                    setIsAddingNew(true);
                                    setEditingChart(null);
                                    setNewChart(defaultChart);
                                }
                            }}
                            className={`flex items-center justify-center gap-2 px-4 py-2 rounded text-sm w-full sm:w-auto transition-colors ${isAddingNew
                                ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                                }`}
                        >
                            <Plus size={16} />
                            {isAddingNew ? "Cancel" : "Add New Size Chart"}
                        </button>
                    </div>
                </div>

                {/* Alerts */}
                {successMessage && (
                    <div className="mb-4 p-3 rounded bg-green-50 border border-green-200 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="text-green-600" />
                            <div className="text-green-800">{successMessage}</div>
                        </div>
                        <button onClick={() => setSuccessMessage("")}>
                            <X size={16} />
                        </button>
                    </div>
                )}

                {error && (
                    <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="text-red-600" />
                            <div className="text-red-800">{error}</div>
                        </div>
                        <button onClick={() => setError(null)}>
                            <X size={16} />
                        </button>
                    </div>
                )}

                {/* Add / Edit form */}
                {(isAddingNew || editingChart) && (
                    <div className="mb-6 bg-white p-4 rounded shadow">
                        <form
                            onSubmit={handleSaveChart}
                            className="grid grid-cols-1 md:grid-cols-2 gap-4"
                        >
                            {/* Basic info */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Chart Name
                                </label>
                                <input
                                    className="w-full mt-1 p-2 border rounded"
                                    value={currentChart.name || ""}
                                    onChange={(e) =>
                                        setCurrentChart({
                                            ...currentChart,
                                            name: e.target.value,
                                        })
                                    }
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Label Group Name
                                </label>
                                <input
                                    className="w-full mt-1 p-2 border rounded"
                                    placeholder="e.g. Sizes, Capacity, Model Type"
                                    value={currentChart.labelName || ""}
                                    onChange={(e) =>
                                        setCurrentChart({
                                            ...currentChart,
                                            labelName: e.target.value,
                                        })
                                    }
                                />
                            </div>

                            {/* Shop ID (optional) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Shop ID (optional)
                                </label>
                                <input
                                    className="w-full mt-1 p-2 border rounded"
                                    placeholder="Leave blank to save as global template"
                                    value={currentChart.shopId || ""}
                                    onChange={(e) =>
                                        setCurrentChart({
                                            ...currentChart,
                                            shopId: e.target.value,
                                        })
                                    }
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    If filled, this size chart belongs to that shop. If empty,
                                    it will be treated as a global template.
                                </p>
                            </div>

                            <div />

                            {/* NEW: Image upload */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Chart Image (optional)
                                </label>
                                <div className="mt-1 flex items-center gap-4 flex-wrap">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="text-sm"
                                    />
                                    {imageUploading && (
                                        <span className="text-xs text-gray-500">
                                            Uploading...
                                        </span>
                                    )}
                                    {currentChart.imageUrl && !imageUploading && (
                                        <img
                                            src={currentChart.imageUrl}
                                            alt="Size chart"
                                            className="h-16 w-auto rounded border object-contain"
                                        />
                                    )}
                                </div>
                                {imageUploadError && (
                                    <p className="mt-1 text-xs text-red-600">
                                        {imageUploadError}
                                    </p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">
                                    This image will be stored with the size chart. On edit, the
                                    previously uploaded image is shown here.
                                </p>
                            </div>

                            {/* Tags */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Tags
                                </label>
                                <div className="mt-1 flex-wrap flex gap-2">
                                    {(currentChart.tags || []).map((tag) => (
                                        <span
                                            key={tag}
                                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 border border-blue-200 text-xs text-blue-800"
                                        >
                                            <TagIcon size={12} />
                                            <span>{tag}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveTag(tag)}
                                            >
                                                <X size={10} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-2 flex gap-2">
                                    <input
                                        type="text"
                                        className="flex-1 px-3 py-2 border rounded text-sm"
                                        placeholder="Type tag and press Enter or ,"
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={handleTagKeyDown}
                                    />
                                    <button
                                        type="button"
                                        className="px-3 py-2 rounded bg-gray-100 text-sm"
                                        onClick={handleAddTag}
                                    >
                                        Add Tag
                                    </button>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    These tags are used when auto-matching size charts with
                                    categories/products.
                                </p>
                            </div>

                            {/* Labels (rows) */}
                            <div className="md:col-span-2">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <ListOrdered size={16} />
                                        <span className="text-sm font-medium text-gray-700">
                                            Labels
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="flex items-center gap-1 text-sm text-blue-600"
                                        onClick={() =>
                                            handleAddLabel(
                                                `Label ${(currentChart.labels || []).length + 1}`
                                            )
                                        }
                                    >
                                        <Plus size={14} />
                                        Quick Add
                                    </button>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {(currentChart.labels || []).map((lbl, idx) => (
                                        <div
                                            key={idx}
                                            className="flex items-center gap-1 border rounded px-2 py-1 bg-gray-50"
                                        >
                                            <input
                                                className="border-none bg-transparent text-xs w-20 focus:outline-none"
                                                value={lbl}
                                                onChange={(e) =>
                                                    handleLabelChange(idx, e.target.value)
                                                }
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveLabel(idx)}
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-2 flex gap-2">
                                    <input
                                        type="text"
                                        className="flex-1 px-3 py-2 border rounded text-sm"
                                        placeholder="Add a label (e.g. S, M, L, XL)"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleAddLabel(e.target.value);
                                                e.target.value = "";
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="px-3 py-2 rounded bg-gray-100 text-sm"
                                        onClick={(e) => {
                                            const input =
                                                e.currentTarget.parentElement.querySelector(
                                                    "input"
                                                );
                                            if (input && input.value.trim()) {
                                                handleAddLabel(input.value);
                                                input.value = "";
                                            }
                                        }}
                                    >
                                        Add Label
                                    </button>
                                </div>
                            </div>

                            {/* Fields (columns) */}
                            <div className="md:col-span-2">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <SlidersHorizontal size={16} />
                                        <span className="text-sm font-medium text-gray-700">
                                            Fields
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="flex items-center gap-1 text-sm text-blue-600"
                                        onClick={handleAddField}
                                    >
                                        <Plus size={14} />
                                        Add Field
                                    </button>
                                </div>

                                {currentChart.fields?.length ? (
                                    <div className="space-y-2">
                                        {currentChart.fields.map((field, idx) => (
                                            <div
                                                key={idx}
                                                className="flex flex-col md:flex-row gap-2 p-2 border rounded bg-gray-50"
                                            >
                                                <div className="flex-1">
                                                    <label className="block text-xs text-gray-600">
                                                        Field Name
                                                    </label>
                                                    <input
                                                        className="w-full mt-1 px-2 py-1 border rounded text-sm"
                                                        value={field.name || ""}
                                                        onChange={(e) =>
                                                            handleFieldNameChange(
                                                                idx,
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <label className="block text-xs text-gray-600">
                                                        Units (comma separated)
                                                    </label>
                                                    <input
                                                        className="w-full mt-1 px-2 py-1 border rounded text-sm"
                                                        placeholder="e.g. inches, cm or watts"
                                                        value={unitsObjectToString(field.units)}
                                                        onChange={(e) =>
                                                            handleFieldUnitsChange(
                                                                idx,
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="flex items-end">
                                                    <button
                                                        type="button"
                                                        className="px-2 py-1 text-xs rounded bg-red-50 text-red-700 border border-red-200"
                                                        onClick={() => handleRemoveField(idx)}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-gray-500">
                                        No fields added yet. Click &quot;Add Field&quot; to
                                        define parameters like Chest Size, Wattage, Capacity,
                                        etc.
                                    </p>
                                )}
                            </div>

                            {/* Measurements table */}
                            {currentChart.labels?.length > 0 &&
                                fieldUnitColumns.length > 0 && (
                                    <div className="md:col-span-2">
                                        <div className="mb-2 flex items-center gap-2">
                                            <SlidersHorizontal size={16} />
                                            <span className="text-sm font-medium text-gray-700">
                                                Values
                                            </span>
                                        </div>
                                        <div className="overflow-x-auto border rounded">
                                            <table className="min-w-full text-xs">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="px-2 py-2 text-left border-b">
                                                            Label
                                                        </th>
                                                        {fieldUnitColumns.map((col, idx) => (
                                                            <th
                                                                key={idx}
                                                                className="px-2 py-2 text-left border-b"
                                                            >
                                                                {col.header}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentChart.labels.map(
                                                        (lbl, labelIdx) => {
                                                            const row =
                                                                currentChart.values?.[labelIdx] ||
                                                                {};
                                                            return (
                                                                <tr
                                                                    key={labelIdx}
                                                                    className="odd:bg-white even:bg-gray-50"
                                                                >
                                                                    <td className="px-2 py-1 border-b">
                                                                        <input
                                                                            className="w-20 px-1 py-1 border rounded text-xs"
                                                                            value={lbl}
                                                                            onChange={(e) =>
                                                                                handleLabelChange(
                                                                                    labelIdx,
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                        />
                                                                    </td>
                                                                    {fieldUnitColumns.map(
                                                                        (col, idx2) => {
                                                                            const m =
                                                                                row
                                                                                    .measurements?.[
                                                                                col.fieldIndex
                                                                                ] || {};
                                                                            const value =
                                                                                m[col.unitKey] ||
                                                                                "";

                                                                            return (
                                                                                <td
                                                                                    key={idx2}
                                                                                    className="px-2 py-1 border-b"
                                                                                >
                                                                                    <input
                                                                                        className="w-full px-1 py-1 border rounded text-xs"
                                                                                        value={value}
                                                                                        onChange={(e) =>
                                                                                            handleMeasurementChange(
                                                                                                labelIdx,
                                                                                                col.fieldIndex,
                                                                                                col.unitKey,
                                                                                                e.target
                                                                                                    .value
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </td>
                                                                            );
                                                                        }
                                                                    )}
                                                                </tr>
                                                            );
                                                        }
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">
                                            Each row corresponds to a label (e.g. S, M, L) and
                                            each column to a field + unit combination.
                                        </p>
                                    </div>
                                )}

                            {/* Actions */}
                            <div className="flex items-center gap-2 md:col-span-2">
                                <button
                                    type="submit"
                                    className="px-4 py-2 rounded bg-blue-600 text-white"
                                    disabled={loading}
                                >
                                    {isEditing ? "Save Changes" : "Create Size Chart"}
                                </button>
                                <button
                                    type="button"
                                    className="px-4 py-2 rounded bg-gray-200"
                                    onClick={resetFormState}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* List of charts */}
                <div className="bg-white p-4 rounded shadow">
                    {loading && !isAddingNew && !editingChart ? (
                        <div className="text-center py-8">
                            <div className="animate-spin border-4 border-blue-600 border-t-transparent rounded-full w-8 h-8 mx-auto mb-3" />
                            <div className="text-blue-700">Loading size charts...</div>
                        </div>
                    ) : filteredCharts.length === 0 ? (
                        <div className="text-center py-6 text-gray-500">
                            No size charts found
                        </div>
                    ) : (
                        <>
                            <div className="space-y-2">
                                {filteredCharts.map((chart) => {
                                    const badge = getScopeBadge(chart);
                                    return (
                                        <div
                                            key={chart._id}
                                            className="rounded-md border bg-white hover:bg-gray-50 p-3 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3"
                                        >
                                            <div className="flex-1 min-w-0 w-full">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <div className="font-semibold text-gray-900 break-words">
                                                        {chart.name}
                                                    </div>
                                                    <span
                                                        className={`text-[10px] px-2 py-0.5 rounded-full ${badge.className}`}
                                                    >
                                                        {badge.label}
                                                    </span>
                                                </div>
                                                <div className="mt-1 text-xs text-gray-600 flex flex-wrap gap-3">
                                                    <span className="inline-flex items-center gap-1">
                                                        <ListOrdered size={12} />
                                                        <span>
                                                            {chart.labelName || "Labels"}:{" "}
                                                            {chart.labels?.length || 0}
                                                        </span>
                                                    </span>
                                                    <span className="inline-flex items-center gap-1">
                                                        <SlidersHorizontal size={12} />
                                                        <span>
                                                            Fields: {chart.fields?.length || 0}
                                                        </span>
                                                    </span>
                                                    {!!chart.tags?.length && (
                                                        <span className="inline-flex items-center gap-1">
                                                            <TagIcon size={12} />
                                                            <span className="break-words">
                                                                Tags: {chart.tags.join(", ")}
                                                            </span>
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Shop details when in shop view */}
                                                {chart.shopId && (
                                                    <div className="mt-1 text-[11px] text-gray-500">
                                                        Shop: {chart.shopId?.name || "-"} | Owner:{" "}
                                                        {chart.shopId?.userId?.fullName || "-"} (
                                                        {chart.shopId?.userId?.phone || "-"})
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-2 w-full sm:w-auto justify-end flex-shrink-0">
                                                {/* Use as template for global charts */}
                                                {!chart.shopId && (
                                                    <button
                                                        title="Use as template"
                                                        className="p-2 rounded bg-gray-50 hover:bg-gray-100"
                                                        onClick={() => handleUseAsTemplate(chart)}
                                                    >
                                                        <Copy size={16} />
                                                    </button>
                                                )}

                                                <button
                                                    title="Edit"
                                                    className="p-2 rounded bg-blue-50 hover:bg-blue-100"
                                                    onClick={() => {
                                                        setIsAddingNew(false);
                                                        setEditingChart({
                                                            ...defaultChart,
                                                            ...chart,
                                                            shopId: chart.shopId
                                                                ? String(
                                                                    chart.shopId._id ||
                                                                    chart.shopId
                                                                )
                                                                : "",
                                                        });
                                                        window.scrollTo({
                                                            top: 0,
                                                            behavior: "smooth",
                                                        });
                                                    }}
                                                >
                                                    <Edit size={16} />
                                                </button>

                                                <button
                                                    title="Delete"
                                                    className="p-2 rounded bg-red-50 hover:bg-red-100"
                                                    onClick={() => {
                                                        setConfirmationModal({
                                                            isOpen: true,
                                                            title: "Confirm Deletion",
                                                            message: `Delete size chart "${chart.name}"? This action cannot be undone.`,
                                                            requireConfirmationText: true,
                                                            onConfirm: async () => {
                                                                setConfirmationModal((prev) => ({
                                                                    ...prev,
                                                                    isOpen: false,
                                                                }));
                                                                await handleDeleteChart(chart);
                                                            },
                                                        });
                                                    }}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Pagination footer */}
                            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-600">
                                <div>
                                    Rows per page:{" "}
                                    <span className="font-medium">{limit}</span>
                                </div>
                                <div>
                                    Showing{" "}
                                    <span className="font-medium">{startIndex}</span> to{" "}
                                    <span className="font-medium">{endIndex}</span> of{" "}
                                    <span className="font-medium">{totalCount}</span> results
                                </div>
                                <div className="flex items-center justify-end gap-2">
                                    <button
                                        className="px-3 py-1 border rounded bg-gray-100 disabled:opacity-50"
                                        onClick={handlePrevPage}
                                        disabled={page <= 1}
                                    >
                                        Previous
                                    </button>
                                    <span>
                                        Page{" "}
                                        <span className="font-medium">{page}</span> of{" "}
                                        <span className="font-medium">{totalPages}</span>
                                    </span>
                                    <button
                                        className="px-3 py-1 border rounded bg-gray-100 disabled:opacity-50"
                                        onClick={handleNextPage}
                                        disabled={page >= totalPages || totalCount === 0}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}